import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../components/common/Button';

interface NotFoundProps {
  title?: string;
  message?: string;
  showBackButton?: boolean;
  showHomeButton?: boolean;
  showSearchSuggestions?: boolean;
  customActions?: React.ReactNode;
  theme?: 'light' | 'dark';
  illustration?: 'default' | 'minimal' | 'detailed';
}

const NotFound: React.FC<NotFoundProps> = ({
  title = 'Page Not Found',
  message = "Sorry, we couldn't find the page you're looking for.",
  showBackButton = true,
  showHomeButton = true,
  showSearchSuggestions = true,
  customActions,
  theme = 'light',
  illustration = 'default'
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [countdown, setCountdown] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Auto-redirect countdown (optional)
  useEffect(() => {
    if (countdown !== null && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      navigate('/');
    }
  }, [countdown, navigate]);

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleStartCountdown = () => {
    setCountdown(5);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to search results or dashboard with search query
      navigate(`/?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  // Common page suggestions based on the application
  const commonPages = [
    { title: 'Dashboard', path: '/', icon: '📊' },
    { title: 'Bookings', path: '/bookings', icon: '📋' },
    { title: 'Fleet Management', path: '/fleet', icon: '🚗' },
    { title: 'Finance', path: '/finance', icon: '💰' },
    { title: 'Configuration', path: '/configuration', icon: '⚙️' }
  ];

  const renderIllustration = () => {
    switch (illustration) {
      case 'minimal':
        return (
          <div className="text-6xl mb-8">🔍</div>
        );
      
      case 'detailed':
        return (
          <div className="mb-8">
            <svg 
              className="w-64 h-64 mx-auto text-gray-400" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={1}
                d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0118 12a8 8 0 01-8 8 8 8 0 01-8-8 7.962 7.962 0 012-5.291m2.291 2A5.962 5.962 0 0012 6a6 6 0 016 6 5.962 5.962 0 01-2.291 2"
              />
            </svg>
          </div>
        );
      
      default:
        return (
          <div className="mb-8">
            <div className="text-9xl font-bold text-gray-300 mb-4">404</div>
            <div className="text-4xl">🤔</div>
          </div>
        );
    }
  };

  const themeClasses = {
    light: {
      container: 'bg-white',
      text: 'text-gray-900',
      subtext: 'text-gray-600',
      border: 'border-gray-200',
      background: 'bg-gray-50'
    },
    dark: {
      container: 'bg-gray-900',
      text: 'text-white',
      subtext: 'text-gray-300',
      border: 'border-gray-700',
      background: 'bg-gray-800'
    }
  };

  const currentTheme = themeClasses[theme];

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 ${currentTheme.background}`}>
      <div className={`max-w-2xl w-full text-center ${currentTheme.container} rounded-2xl shadow-xl p-8 lg:p-12`}>
        {/* Illustration */}
        {renderIllustration()}

        {/* Title and Message */}
        <h1 className={`text-3xl lg:text-4xl font-bold mb-4 ${currentTheme.text}`}>
          {title}
        </h1>
        
        <p className={`text-lg mb-2 ${currentTheme.subtext}`}>
          {message}
        </p>

        {/* Current Path Info */}
        <p className={`text-sm mb-8 ${currentTheme.subtext} font-mono bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded inline-block`}>
          Requested: {location.pathname}
        </p>

        {/* Countdown */}
        {countdown !== null && countdown > 0 && (
          <div className={`mb-6 p-4 ${currentTheme.border} border rounded-lg`}>
            <p className={currentTheme.subtext}>
              Redirecting to home page in {countdown} seconds...
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
          {showBackButton && (
            <Button
              variant="outline"
              size="lg"
              onClick={handleGoBack}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              }
            >
              Go Back
            </Button>
          )}
          
          {showHomeButton && (
            <Button
              variant="primary"
              size="lg"
              onClick={handleGoHome}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              }
            >
              Go Home
            </Button>
          )}

          {!countdown && (
            <Button
              variant="ghost"
              size="lg"
              onClick={handleStartCountdown}
            >
              Auto-redirect (5s)
            </Button>
          )}
        </div>

        {/* Custom Actions */}
        {customActions && (
          <div className="mb-8">
            {customActions}
          </div>
        )}

        {/* Search Suggestions */}
        {showSearchSuggestions && (
          <div className="space-y-6">
            {/* Search Form */}
            <div>
              <form onSubmit={handleSearch} className="flex gap-2 max-w-md mx-auto">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for pages..."
                  className={`
                    flex-1 px-4 py-2 border rounded-lg
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                    ${currentTheme.border} ${currentTheme.text}
                  `}
                />
                <Button type="submit" variant="outline">
                  Search
                </Button>
              </form>
            </div>

            {/* Common Pages */}
            <div>
              <h3 className={`text-lg font-medium mb-4 ${currentTheme.text}`}>
                Popular Pages
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {commonPages.map((page) => (
                  <button
                    key={page.path}
                    onClick={() => navigate(page.path)}
                    className={`
                      p-4 border rounded-lg text-left transition-all duration-200
                      hover:border-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900
                      focus:outline-none focus:ring-2 focus:ring-blue-500
                      ${currentTheme.border}
                    `}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{page.icon}</span>
                      <div>
                        <div className={`font-medium ${currentTheme.text}`}>
                          {page.title}
                        </div>
                        <div className={`text-sm ${currentTheme.subtext}`}>
                          {page.path}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Help Text */}
        <div className={`mt-8 pt-6 border-t ${currentTheme.border}`}>
          <p className={`text-sm ${currentTheme.subtext}`}>
            If you believe this is an error, please contact support or try refreshing the page.
          </p>
        </div>

        {/* Debug Info (only in development) */}
        {process.env.NODE_ENV === 'development' && (
          <details className={`mt-4 text-left text-xs ${currentTheme.subtext}`}>
            <summary className="cursor-pointer">Debug Info</summary>
            <pre className="mt-2 p-2 bg-gray-100 dark:bg-gray-800 rounded overflow-x-auto">
              {JSON.stringify({
                pathname: location.pathname,
                search: location.search,
                hash: location.hash,
                state: location.state,
                timestamp: new Date().toISOString()
              }, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};

// Alternative minimal 404 component
export const MinimalNotFound: React.FC = () => (
  <NotFound
    title="404"
    message="Page not found"
    illustration="minimal"
    showSearchSuggestions={false}
  />
);

// Error boundary 404 component
export const ErrorNotFound: React.FC<{ error?: Error }> = ({ error }) => (
  <NotFound
    title="Something went wrong"
    message={error?.message || "An unexpected error occurred"}
    showSearchSuggestions={false}
    customActions={
      <Button
        variant="outline"
        onClick={() => window.location.reload()}
        icon={
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        }
      >
        Reload Page
      </Button>
    }
  />
);

export default NotFound;